<template>
  <el-card shadow="hover" class="chart-card">
    <div slot="header" class="card-header">
      <span>{{ title || '客户揽收效率监控 (Observação da situação de coleta dos clientes)' }}</span>
      <img :src="exportIcon" class="export-btn" alt="导出" @click="handleExport" />
    </div>
    <base-echart :options="chartOption" height="290px" />
  </el-card>
</template>

<script>
import BaseEchart from './BaseEchart'

/**
 * 客户揽收效率监控图表组件
 * 展示各客户的揽收率和揽收及时率对比
 */
export default {
  name: 'CustomerEfficiencyChart',
  components: { BaseEchart },
  props: {
    title: {
      type: String,
      default: '',
      description: '图表标题'
    },
    chartData: {
      type: Array,
      default: () => [],
      description: '客户揽收效率数据'
    }
  },
  data() {
    return {
      chartOption: {}, // ECharts配置对象
      exportIcon: require('@/assets/images/kanban/endKanbanBoard/Export.svg')
    }
  },
  watch: {
    chartData: {
      handler() {
        this.updateChartData()
      },
      deep: true
    }
  },
  created() {
    // 组件创建时初始化图表配置
    this.initChartOption()
  },
  methods: {
    /**
     * 初始化图表配置
     * 配置双柱状图，展示揽收率和揽收及时率（百分比）
     */
    initChartOption() {
      this.chartOption = {
        color: ['#2AAB80', '#4581DE'], // 绿色和蓝色配色
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            let result = params[0].axisValueLabel + '<br/>'
            params.forEach((param) => {
              result += param.marker + param.seriesName + ': ' + param.value.toFixed(2) + '%<br/>'
            })
            return result
          }
        }, // 坐标轴触发，显示百分比（保留2位小数）
        legend: {
          data: ['揽收率 Taxa de coleta', '揽收及时率 Taxa de coleta pontual'],
          top: 0,
          icon: 'circle',
          textStyle: {
            fontFamily: 'Alibaba PuHuiTi 2.0, Alibaba PuHuiTi 20',
            fontWeight: 'normal',
            fontSize: 14,
            color: '#A8AEB9'
          }
        }, // 图例配置
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }, // 网格配置，包含坐标轴标签
        dataZoom: [
          {
            type: 'slider',
            show: false, // 初始不显示，在updateChartData中根据数据量决定
            xAxisIndex: [0],
            start: 0,
            end: 100, // 初始值100%，将在updateChartData中根据实际数据量更新
            height: 20,
            bottom: 5,
            borderColor: '#ccc',
            fillerColor: 'rgba(42, 171, 128, 0.2)',
            handleStyle: { color: '#2AAB80' }
          }
        ],
        xAxis: {
          type: 'category',
          data: [],
          axisLine: { lineStyle: { color: '#D9D9D9', type: 'dashed' } },
          axisTick: { show: false },
          axisLabel: {
            fontFamily: 'Alibaba PuHuiTi 2, Alibaba PuHuiTi 20',
            fontWeight: 'normal',
            fontSize: 14,
            color: '#6A7382'
          }
        },
        yAxis: {
          type: 'value',
          max: 100, // 最大值100%
          axisLabel: { formatter: '{value}%' }, // 显示百分号
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#E0E6F1' } } // 虚线网格
        },
        series: [
          {
            name: '揽收率 Taxa de coleta', // 揽收率
            type: 'bar',
            data: [], // 清空默认数据
            barWidth: 10, // 柱宽
            itemStyle: {
              borderRadius: [5, 5, 0, 0], // 顶部圆角
              shadowColor: 'rgba(42, 171, 128, 0.8)',
              shadowBlur: 6,
              shadowOffsetY: 3 // 阴影效果
            }
          },
          {
            name: '揽收及时率 Taxa de coleta pontual', // 揽收及时率
            type: 'bar',
            data: [],
            barWidth: 10,
            itemStyle: {
              borderRadius: [5, 5, 0, 0],
              shadowColor: 'rgba(69, 129, 222, 0.8)',
              shadowBlur: 6,
              shadowOffsetY: 3
            }
          }
        ]
      }
    },

    /**
     * 更新图表数据
     * 根据props中的真实数据更新图表
     */
    updateChartData() {
      // 当数据为空时，显式清空图表数据
      if (!this.chartData || this.chartData.length === 0) {
        this.chartOption.xAxis.data = []
        this.chartOption.series[0].data = []
        this.chartOption.series[1].data = []
        this.chartOption.dataZoom[0].show = false
        this.chartOption.grid.bottom = '3%'
        // 触发图表更新
        this.chartOption = { ...this.chartOption }
        return
      }

      const defaultShowCount = 5 // 默认显示5条数据
      const showScrollBarThreshold = 6 // 超过6条才显示滚动条

      // 根据API返回的字段更新数据，将小数转换为百分比
      const customerNames = this.chartData.map((item) => item.customerName || item.name || '')
      const collectionRate = this.chartData.map((item) => {
        const rate = item.collectionRate || item.collectRate || 0
        return Math.round(Number(rate) * 10000) / 100 // 修正浮点数精度问题
      })
      const timelyRate = this.chartData.map((item) => {
        const rate = item.timelyRate || item.collectTimelyRate || item.collectionOnTimeRate || 0
        return Math.round(Number(rate) * 10000) / 100 // 修正浮点数精度问题
      })

      this.chartOption.xAxis.data = customerNames
      this.chartOption.series[0].data = collectionRate
      this.chartOption.series[1].data = timelyRate

      // 判断是否显示滚动条（超过6条才显示）
      const showScrollBar = this.chartData.length > showScrollBarThreshold
      this.chartOption.dataZoom[0].show = showScrollBar

      // 计算并更新dataZoom end值
      // 6条及以下全部显示，超过6条才显示前5条
      const endPercent =
        this.chartData.length <= showScrollBarThreshold
          ? 100
          : Math.min(100, (defaultShowCount / this.chartData.length) * 100)
      this.chartOption.dataZoom[0].end = endPercent

      // 动态调整grid的bottom值
      this.chartOption.grid.bottom = showScrollBar ? '15%' : '3%'

      // 触发图表更新
      this.chartOption = { ...this.chartOption }
    },

    /**
     * 处理导出
     */
    handleExport() {
      this.$emit('export')
    }
  }
}
</script>

<style scoped lang="scss">
.chart-card {
  margin-bottom: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: none;

  ::v-deep .el-card__header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
    font-weight: normal;
    font-size: 16px;
    color: #0f1728;
    font-style: normal;
    text-transform: none;
    font-weight: bold;
    .export-btn {
      width: 16px;
      height: 16px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.7;
      }
    }
  }
}
</style>
